import { startTimeDesc } from 'components/Search/const';
import { qsOptions } from 'providers/clients/httpClient';
import { TSessionConfigData } from 'providers/contexts/CosmosContext';
import qs from 'qs';
import { Location, matchPath } from 'react-router';
import { isDeepEqual } from 'remeda';
import { KeyStringAny } from 'utility/types/cosmosTypes';
import { hasBetaProgram } from 'utility/experiments';
import { GetUserMeData } from 'utility/types/dataProvider';
import {
  getDesignAppNavCfg,
  getEditorialNavCfg,
  getRootNavCfg,
  getUserAppNavCfg,
  KeyBoolMap,
  NavConfig,
  NavConfigFnParams,
  NavItem,
} from './LeftNav';

// Get the nav config based on the app. returned by getAppViaLocation.
export function getNavConfig({
  app,
  sessionConfig,
  userInfo,
  isAiSearchActive,
}: NavConfigFnParams): NavConfig {
  const hasBetaAccess = hasBetaProgram(sessionConfig, userInfo);

  let baseNavCfg: NavConfig;
  switch (app) {
    case 'editorial':
    case 'notifier':
    case 'feeds': {
      baseNavCfg = getEditorialNavCfg({ app, sessionConfig, isAiSearchActive });
      break;
    }

    case 'reports':
    case 'services':
    case 'journeys':
    case 'community':
    case 'emailreach':
    case 'user':
    case 'transactions': {
      baseNavCfg = getUserAppNavCfg({ app, sessionConfig });
      break;
    }

    case 'design': {
      baseNavCfg = getDesignAppNavCfg({ app, sessionConfig });
      break;
    }

    // TODO: CMSADMIN-1460 - dashboard root nav config
    case 'dashboards':
      baseNavCfg = getRootNavCfg({ app, sessionConfig });
      break;

    case 'root':
    case 'settings': {
      baseNavCfg = getRootNavCfg({ app, sessionConfig });
      break;
    }

    default: {
      // Default to root nav so at least something is shown.
      baseNavCfg = getRootNavCfg({ app, sessionConfig });
      break;
    }
  }

  return applyBetaDisable(baseNavCfg, hasBetaAccess);
}

// Recursively walk nav config and, for any item with beta === true and hasBetaAccess === true,
// set disableLink = true. This ensures beta items are marked disabled when the user does NOT
// have beta access. Note: we mutate a shallow copy structure to avoid mutating original configs.
function applyBetaDisable(navCfg: NavConfig, hasBetaAccess: boolean): NavConfig {
  // navCfg is [setup, ...items]
  const [setup, ...items] = navCfg;

  function walk(item: NavItem): NavItem {
    // Make a shallow copy to avoid mutating original references
    const copy: NavItem = { ...item };

    // If item is beta and user does NOT have beta access, ensure disableLink=true
    if (copy.beta && !hasBetaAccess) {
      copy.disableLink = true as true;
    }

    if (Array.isArray(copy.children) && copy.children.length) {
      copy.children = copy.children.map(walk);
    }

    return copy;
  }

  const newItems = items.map((it) => walk(it));
  return [setup, ...newItems];
}

// Get the app from the location path.
export function getAppViaLocation(location: string, activeSite: string) {
  const segments = location.split('/').filter(Boolean);
  const activeSiteIndex = segments.indexOf(activeSite);
  // Check if the array is just the default site
  if (segments.length === 1 && segments[0] === activeSite) {
    return 'root';
  }
  // Check if activeSiteIndex wasn't found in the path
  if (activeSiteIndex === -1) {
    return 'root';
  }
  // Return the first segment after the default site (editorial, user, etc.) else return root "/" as a safe fallback.
  const result = segments.length > 1 ? segments[activeSiteIndex + 1] : 'root';
  return result;
}

type SetupNavItemsArgs = {
  locationPathname: string;
  activeSite: string;
  sessionConfig?: TSessionConfigData;
  userInfo?: GetUserMeData;
  isAiSearchActive?: boolean;
};

// Helper function to setup the nav items for the LeftNav component.
export function setupNavItems({
  locationPathname,
  activeSite,
  sessionConfig,
  userInfo,
  isAiSearchActive,
}: SetupNavItemsArgs) {
  const currentApp = getAppViaLocation(locationPathname, activeSite);
  const navItemsConfig = getNavConfig({
    app: currentApp,
    sessionConfig: sessionConfig,
    userInfo: userInfo,
    isAiSearchActive: isAiSearchActive,
  });
  const [navSetup, ...navItems] = navItemsConfig;
  // Determine if we have a header, and extract it and slice the rest if we do.
  const header = navSetup.hasHeader ? navItems[0] : null;
  const items = navItems.slice(navSetup.hasHeader ? 1 : 0);
  return { currentApp, items, header };
}

/**
 * Normalizes a URL/path string for matching helpers.
 * See test cases for example input/output.
 * It removes trailing slashes from the path portion
 * while preserving any query parameters and fragments.
 */
export function normalizeUrlPattern(s: string) {
  // Ensure input is a string
  if (typeof s !== 'string') {
    return '';
  }
  // If the string is empty, return an empty string.
  if (s === '') {
    return '';
  }
  // Separate the fragment (hash) if it exists.
  const fragmentIndex = s.indexOf('#');
  let pathAndQuery = s;
  let fragment = '';
  if (fragmentIndex !== -1) {
    fragment = s.substring(fragmentIndex);
    pathAndQuery = s.substring(0, fragmentIndex);
  }
  // Separate the query string from the remaining part.
  const queryStringIndex = pathAndQuery.indexOf('?');
  let path = pathAndQuery;
  let queryString = '';
  if (queryStringIndex !== -1) {
    queryString = pathAndQuery.substring(queryStringIndex);
    path = pathAndQuery.substring(0, queryStringIndex);
  }
  // Regex to remove trailing slashes from the path part only.
  let normalizedPath = path.replace(/\/+$/, '');
  // Handles the edge case if the path was just slashes (e.g., '/' or '///').
  // In that case would be empty, so should default it to '/'.
  if (path.length > 0 && normalizedPath.length === 0) {
    normalizedPath = '/';
  }
  // Recombine the normalized path, the query string, and the fragment.
  return normalizedPath + queryString + fragment;
}

const STRICT_PATTERN_PREFIX = 'STRICT::';

type MatchUrlActiveParams = {
  pattern: string;
  activeSite: string;
  locationObj: Location;
};

/**
 * Robust URL matching for nav items.
 * Handles wildcards, query params (order-insensitive), hash, and strict path.
 * @param pattern Pattern from nav config (e.g., '/editorial/search?type=foo')
 * @param activeSite Tenant/site string (e.g., 'test.cms.us-corp-dev-3.vip.tndev.net')
 * @param locationObj Location from react-router (output of useLocation)
 */
export function matchUrlActive({
  pattern,
  activeSite,
  locationObj,
}: MatchUrlActiveParams): boolean {
  try {
    const locPath = locationObj.pathname;
    const locSearch = locationObj.search;
    const locHash = locationObj.hash;

    /**
     * Normalized pattern, remove strict prefix if present.
     * Use nPattern everywhere below this point.
     */
    const isStrictPattern = pattern.startsWith(STRICT_PATTERN_PREFIX);
    const nPattern = isStrictPattern
      ? pattern.replace(STRICT_PATTERN_PREFIX, '')
      : pattern;

    const isWildcardPattern = nPattern.endsWith('*');
    const isQueryHashPattern = nPattern.includes('?') || nPattern.includes('#');

    /**
     * Wildcard: always use matchPath, partial allowed (end: false)
     * Note since patterns incoming dont have activesite, we must prefix it.
     */
    if (isWildcardPattern) {
      const isWildcardMatch = Boolean(
        matchPath(
          { path: `/${activeSite}${nPattern}`, end: false, caseSensitive: false },
          locationObj.pathname,
        ),
      );
      return isWildcardMatch;
    }

    /**
     * Query/hash/strict match: use URL for comparison
     * We must ignore order in search qp and normalize slashes
     */
    if (isQueryHashPattern || isStrictPattern) {
      // Use activeSite as origin prefix; add pattern, patterns shoulds always be relative.
      const origin = `https://${activeSite}`;

      // Ensure leading slash after activeSite
      const prefixedPattern = nPattern.startsWith('/')
        ? `/${activeSite}${nPattern}`
        : `/${activeSite}/${nPattern}`;

      const patternUrl = new URL(prefixedPattern, origin);
      const actualUrl = new URL(`${locPath}${locSearch}${locHash}`, origin);

      const normPattern = normalizeUrlPattern(patternUrl.pathname);
      const normActual = normalizeUrlPattern(actualUrl.pathname);

      // Normalize the paths (remove trailing slashes, etc).
      // If they dont equal, cant be a match.
      if (normPattern !== normActual) {
        return false;
      }

      // Compare query params (ignoring qp order)
      // as some libraries sort the qp, others dont, etc, but effect is the same.
      const patternParams = Object.fromEntries(patternUrl.searchParams.entries());
      const actualParams = Object.fromEntries(actualUrl.searchParams.entries());

      if (!isDeepEqual(patternParams, actualParams)) {
        return false;
      }

      // Compare hash
      if (patternUrl.hash && patternUrl.hash !== actualUrl.hash) {
        return false;
      }

      return true;
    }

    /**
     * Default matchPath via react-router:
     * end true, no wildcards/query/hash considerations.
     */
    const isNormalMatch = Boolean(
      matchPath(
        { path: `/${activeSite}${nPattern}`, end: true, caseSensitive: false },
        locPath,
      ),
    );
    return isNormalMatch;
  } catch (error) {
    console.error('[matchUrlActive] Error:', error);
    return false;
  }
}

interface GetActiveOptions {
  items: NavItem[];
  activeSite: string;
  locationObj: Location;
}

interface GetActiveOptionsReturn {
  activeKeys: KeyBoolMap;
  openKeys: KeyBoolMap;
}
/**
 * Determines which nav items are "active" (highlighted) and which categories are "open".
 * - Recursively check children before parents (so categories reflect child state).
 * - Sets categories as active/open if any child is active, or if their own matchPaths match.
 * - A category is only "open" if (a) any child is active or (b) its own matchPaths match.
 * - Only top-level and child items that are matched will be considered active/open.
 */
export function getActiveOpenNavItems({
  items,
  activeSite,
  locationObj,
}: GetActiveOptions): GetActiveOptionsReturn {
  const activeKeys: KeyBoolMap = {};
  const openKeys: KeyBoolMap = {};

  // Recursive helper
  function checkItems(list: NavItem[], parentKey?: string) {
    for (const item of list) {
      // Skip divider/separator items
      if (item?.divider) {
        continue;
      }

      // active check flag
      let isActive = false;

      // Check children first
      // If any child is active, category is active/open as well.
      if (Array.isArray(item?.children) && item?.children?.length) {
        // Recursively check children
        checkItems(item?.children, item?.reactKey);

        // If any child is active, mark this category as active/open
        if (item?.children?.some((child) => activeKeys[child.reactKey])) {
          openKeys[item.reactKey] = true;
          activeKeys[item.reactKey] = true;
          // So parent's own matchPaths dont re-mark
          isActive = true;
        }
      }

      // Check this item itself, run only if not already made active by a child.
      if (
        !isActive &&
        Array.isArray(item?.matchPaths) &&
        // Check if any of the matchPaths match the current location.
        item?.matchPaths?.some((pat) =>
          matchUrlActive({
            pattern: pat,
            activeSite: activeSite,
            locationObj: locationObj,
          }),
        )
      ) {
        // If it matches, mark it active.
        activeKeys[item.reactKey] = true;
        // If this item has a parent (is a child), mark parent open
        if (parentKey) {
          openKeys[parentKey] = true;
        }
        // If this is is category (has children) mark self open
        if (Array.isArray(item?.children) && item?.children?.length) {
          openKeys[item.reactKey] = true;
        }
      }
    }
  }

  // Start at root of items list
  checkItems(items);

  return { activeKeys, openKeys };
}

/**
 * Creates a query parameter string from an object and path.
 * path: prefix path (e.g., '/editorial/search')
 * params: object to stringify as query params
 * eg input: createQpStr('/editorial/search', { dir: 'DESC', sort: 'start_time', type: 'video' })
 * eg output: /editorial/search?dir=DESC&sort=start_time&type=video
 * Returns path string as provided if no params are given.
 */

export const createQpStr = (path: string, params?: KeyStringAny) => {
  const payload = params ?? {};
  const hasPayload = Boolean(Object.keys(payload).length);
  if (!hasPayload) {
    return path;
  }
  const result = `${path}?${qs.stringify(payload, qsOptions)}`;
  return result;
};

/**
 * editorial search leftnav shortcut paths setup
 * with default filter/sort, applied as needed.
 *
 * Strip sort params from shortcut links when AI search (beta)
 * is active so results are ordered by relevance instead of start_time.
 */

type GetEditorialSearchShortcutPathsArgs = {
  isAiSearchActive?: boolean;
};

export const getEditorialSearchShortcutPaths = ({
  isAiSearchActive,
}: GetEditorialSearchShortcutPathsArgs) => {
  const editorialSearchPath = '/editorial/search';
  const editorialTemplatesManagePath = `${editorialSearchPath}/templates`;

  const sortDirQp = isAiSearchActive ? null : startTimeDesc;
  const articleParams = { type: 'article', ...(sortDirQp ?? {}) };
  const imageParams = { type: 'image', ...(sortDirQp ?? {}) };
  const videoParams = { type: 'video', ...(sortDirQp ?? {}) };
  const collectionParams = { type: 'collection', ...(sortDirQp ?? {}) };

  /**
   * "All Assets" shortcut - only highlights when the base
   * search route has no query params (eg: "/editorial/search")
   *
   * When we have no other params, we prefix with "STRICT::".
   * this forces matchUrlActive to take the stricter matching
   * behavior, preventing the base shortcut from being highlighted
   * when on a filtered page (eg: "/editorial/search?type=video").
   */
  const editorialSearchPathMatch = sortDirQp
    ? createQpStr(editorialSearchPath, sortDirQp)
    : `${STRICT_PATTERN_PREFIX}${editorialSearchPath}`;

  // shortcut paths with specific type filters
  const editorialSearchArticlePath = createQpStr(editorialSearchPath, articleParams);
  const editorialSearchImagePath = createQpStr(editorialSearchPath, imageParams);
  const editorialSearchVideoPath = createQpStr(editorialSearchPath, videoParams);
  const editorialSearchCollectionPath = createQpStr(
    editorialSearchPath,
    collectionParams,
  );

  return {
    editorialSearchPath,
    editorialSearchPathMatch,
    editorialSearchArticlePath,
    editorialSearchImagePath,
    editorialSearchVideoPath,
    editorialSearchCollectionPath,
    editorialTemplatesManagePath,
  };
};
